/**
 * HighResTexturesContext - Global preloading of high-res textures
 * 
 * Loads high-res library ONCE at app startup and shares across all pages.
 * This ensures thumbnails are instantly available in PixelSculpt, TextureStudio, and CTMSlicer.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  getCachedHighResLibrary, 
  setCachedHighResLibrary,
  preloadThumbnails 
} from '../utils/textureCache';

const HighResTexturesContext = createContext(null);

export function HighResTexturesProvider({ children }) {
  const [textures, setTextures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [thumbnailsPreloaded, setThumbnailsPreloaded] = useState(false);

  // Preload all thumbnails after data is loaded
  const preloadAllThumbnails = useCallback((textureList) => {
    if (!textureList || textureList.length === 0) return;
    
    // Build list of ALL thumbnail URLs using CDN paths
    const thumbnailUrls = textureList.map(tex => 
      tex.cdnThumbnail || tex.cdnPath || `${process.env.REACT_APP_BACKEND_URL}${tex.thumbnail || tex.path}`
    ).filter(Boolean);
    
    console.log(`[HighResContext] Preloading ${thumbnailUrls.length} thumbnails...`);
    preloadThumbnails(thumbnailUrls);
    setThumbnailsPreloaded(true);
  }, []);

  // Load high-res library
  const loadLibrary = useCallback(async (forceRefresh = false) => {
    // Check cache first (unless forced refresh)
    if (!forceRefresh) {
      const cached = getCachedHighResLibrary();
      if (cached) {
        const textureList = cached.textures || (Array.isArray(cached) ? cached : []);
        setTextures(textureList);
        if (cached.categories) setCategories(cached.categories);
        if (cached.categoryCounts) setCategoryCounts(cached.categoryCounts);
        setIsLoading(false);
        setIsReady(true);
        console.log(`[HighResContext] Loaded ${textureList.length} textures from cache`);
        
        // Preload thumbnails even from cache
        preloadAllThumbnails(textureList);
        return;
      }
    }
    
    setIsLoading(true);
    try {
      console.log('[HighResContext] Fetching high-res library from API...');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/texture-studio/highres-library`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }
      
      const data = await response.json();
      const textureList = data.textures || [];
      
      setTextures(textureList);
      if (data.categories) setCategories(data.categories);
      if (data.categoryCounts) setCategoryCounts(data.categoryCounts);
      
      // Cache the result
      setCachedHighResLibrary(data);
      
      console.log(`[HighResContext] Loaded ${textureList.length} textures from API`);
      
      // Preload ALL thumbnails
      preloadAllThumbnails(textureList);
      
      setError(null);
      setIsReady(true);
    } catch (err) {
      console.error('[HighResContext] Load error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [preloadAllThumbnails]);

  // Load on mount
  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  const value = {
    textures,
    categories,
    categoryCounts,
    isLoading,
    isReady,
    error,
    thumbnailsPreloaded,
    // Refresh library (force fetch from API)
    refresh: () => loadLibrary(true),
    // Get texture URL (CDN preferred)
    getTextureUrl: (texture) => {
      if (!texture) return null;
      return texture.cdnPath || texture.cdnThumbnail || `${process.env.REACT_APP_BACKEND_URL}${texture.path || texture.thumbnail}`;
    },
    // Get thumbnail URL (CDN preferred)  
    getThumbnailUrl: (texture) => {
      if (!texture) return null;
      return texture.cdnThumbnail || texture.cdnPath || `${process.env.REACT_APP_BACKEND_URL}${texture.thumbnail || texture.path}`;
    }
  };

  return (
    <HighResTexturesContext.Provider value={value}>
      {children}
    </HighResTexturesContext.Provider>
  );
}

export function useHighResTextures() {
  const context = useContext(HighResTexturesContext);
  if (!context) {
    // Fallback for backwards compatibility
    return {
      textures: [],
      categories: [],
      categoryCounts: {},
      isLoading: false,
      isReady: false,
      error: null,
      thumbnailsPreloaded: false,
      refresh: () => Promise.resolve(),
      getTextureUrl: () => null,
      getThumbnailUrl: () => null
    };
  }
  return context;
}

export default HighResTexturesContext;
